/**
 * Traduction du catalogue d'erreurs de l'API en réactions d'interface.
 *
 * Règle d'or de l'intégration : **on branche la logique sur `error.code`,
 * jamais sur `error.message`**. Les messages de l'API sont en anglais, faits
 * pour un développeur, et peuvent être reformulés sans préavis ; les codes
 * sont stables et versionnés.
 *
 * Ce fichier est la seule frontière entre les codes de l'API et les phrases
 * que voit un visiteur. Chaque code y porte trois informations :
 *
 * - `message` : ce qu'on affiche au visiteur, en français ;
 * - `hint` : la marche à suivre, quand elle existe ;
 * - `retryable` : peut-on proposer un bouton « réessayer » ?
 *
 * La colonne « Réaction front recommandée » du catalogue officiel
 * (docs → Développeurs → Codes d'erreur) est reprise en commentaire de
 * chaque entrée.
 */

import { DashLiteApiError } from '@dash-lite/sdk';

export type ErrorPresentation = {
    /** Message destiné au visiteur. */
    message: string;
    /** Complément d'information ou marche à suivre. */
    hint?: string;
    /** `true` si un nouvel essai a une chance d'aboutir. */
    retryable: boolean;
    /**
     * `true` si l'erreur vient d'une mauvaise configuration du front ou du
     * site, et non d'une action du visiteur. On l'affiche alors comme une
     * erreur technique explicite plutôt que comme un message rassurant :
     * masquer un problème de configuration le rend introuvable.
     */
    configuration?: boolean;
};

/**
 * Catalogue complet. Les clés couvrent l'union `DashLiteErrorCode` du SDK,
 * plus `company_provider_unconfigured`, documenté côté plateforme.
 */
const CATALOGUE: Record<string, ErrorPresentation> = {
    // 401 — Header X-Site-Key absent, malformé ou inconnu.
    // → Erreur de configuration : vérifier la variable d'environnement.
    invalid_site_key: {
        message: "La clé de site utilisée par ce front n'est pas reconnue.",
        hint: 'Vérifiez la variable `VITE_DASHLITE_SITE_KEY` : elle doit contenir la clé affichée dans la console Dash Lite pour ce site.',
        retryable: false,
        configuration: true,
    },

    // 403 — Token d'un environnement utilisé avec la clé de l'autre.
    // → Vider la session locale.
    environment_mismatch: {
        message: "Votre session provient d'un autre environnement que celui de ce site.",
        hint: 'La session locale a été vidée automatiquement. Reconnectez-vous.',
        retryable: false,
        configuration: true,
    },

    // 403 — Site suspendu (clé live).
    // → Page « service momentanément indisponible ».
    site_suspended: {
        message: 'Le service est momentanément indisponible.',
        hint: 'Nous rétablissons l’accès au plus vite. Merci de réessayer un peu plus tard.',
        retryable: true,
    },

    // 401 — Token absent, révoqué ou invalide.
    // → Rediriger vers la connexion, purger le token stocké.
    unauthenticated: {
        message: 'Votre session a expiré.',
        hint: 'Reconnectez-vous pour continuer.',
        retryable: false,
    },

    // 401 — Email ou mot de passe incorrect.
    // → Proposer le mot de passe oublié.
    invalid_credentials: {
        message: 'Identifiants incorrects.',
        hint: 'Vérifiez votre adresse email et votre mot de passe, ou utilisez le lien « mot de passe oublié ».',
        retryable: false,
    },

    // 403 — Action non autorisée pour cet utilisateur.
    // → Masquer l'action ; ne pas réessayer.
    forbidden: {
        message: "Cette action ne vous est pas autorisée.",
        retryable: false,
    },

    // 403 — Fiche complète demandée sans droit d'accès actif.
    // → Rediriger vers la page tarifs / le tunnel de paiement.
    entitlement_required: {
        message: 'Cette fiche complète nécessite un accès actif.',
        hint: 'Choisissez l’offre du site pour débloquer la consultation.',
        retryable: false,
    },

    // 422 — Corps de requête invalide.
    // → Afficher les erreurs champ par champ depuis `details`.
    validation_failed: {
        message: 'Certaines informations saisies sont invalides.',
        retryable: false,
    },

    // 409 — Offre non configurée, ou non synchronisée avec Stripe.
    // → Recharger GET /offer et masquer le bouton d'achat.
    offer_not_available: {
        message: "L'offre n'est pas disponible à l'achat pour le moment.",
        hint: 'Rechargez la page dans quelques instants.',
        retryable: true,
    },

    // 422 — URL de retour hors des domaines déclarés du site.
    // → Corriger la configuration du front.
    invalid_redirect_url: {
        message: "L'adresse de retour n'est pas autorisée pour ce site.",
        hint: 'Le domaine de ce front doit être déclaré dans la configuration du site (console Dash Lite → Domaines).',
        retryable: false,
        configuration: true,
    },

    // 503 — Clés Stripe du site absentes pour cet environnement.
    // → Contacter l'administrateur du site.
    payments_not_configured: {
        message: 'Le paiement est momentanément indisponible sur ce site.',
        hint: 'Les clés Stripe du site ne sont pas configurées pour cet environnement.',
        retryable: false,
        configuration: true,
    },

    // 404 — Ressource ou route inexistante.
    not_found: {
        message: 'Ressource introuvable.',
        retryable: false,
    },

    // 404 — Identifiant national inconnu ou mal formé.
    // → « entreprise introuvable », proposer une nouvelle recherche.
    company_not_found: {
        message: 'Aucune entreprise ne correspond à cet identifiant.',
        hint: 'Vérifiez le numéro saisi, ou lancez une nouvelle recherche par le nom.',
        retryable: false,
    },

    // 503 — Fournisseur de données momentanément indisponible.
    // → « réessayez dans quelques instants », prévoir un bouton réessayer.
    upstream_unavailable: {
        message: 'La source de données officielle ne répond pas.',
        hint: 'C’est temporaire. Réessayez dans quelques instants.',
        retryable: true,
    },

    // 503 — Fournisseur non configuré côté plateforme (clé d'API manquante).
    // → Erreur de configuration NON transitoire : ne pas réessayer en boucle.
    company_provider_unconfigured: {
        message: 'La source de données officielle n’est pas configurée.',
        hint: 'Ce n’est pas un incident passager : l’administrateur de la plateforme doit renseigner la clé du fournisseur.',
        retryable: false,
        configuration: true,
    },

    // 429 — Trop de requêtes.
    // → Attendre le délai de `Retry-After` ; débouncer la recherche.
    rate_limited: {
        message: 'Trop de requêtes en peu de temps.',
        hint: 'Patientez quelques secondes avant de réessayer.',
        retryable: true,
    },

    // 422 — Token de réinitialisation invalide ou expiré.
    // → Proposer de redemander un email de réinitialisation.
    invalid_reset_token: {
        message: 'Ce lien de réinitialisation n’est plus valide.',
        hint: 'Les liens expirent au bout d’un moment. Demandez-en un nouveau.',
        retryable: false,
    },

    // 403 — Paiement refusé par le contrôle anti-fraude, avant le débit.
    // → Ne pas réessayer automatiquement.
    payment_flagged_fraud: {
        message: 'Transaction refusée pour des raisons de sécurité.',
        hint: 'Aucun montant n’a été débité. Contactez notre support si vous pensez qu’il s’agit d’une erreur.',
        retryable: false,
    },

    // 409 — Un compte existe déjà pour cet email lors d'un paiement invité.
    // → Proposer la connexion, ou le lien « mot de passe oublié ».
    account_exists: {
        message: 'Un compte existe déjà avec cette adresse email.',
        hint: 'Connectez-vous, ou utilisez le lien « mot de passe oublié » si vous ne l’avez jamais défini.',
        retryable: false,
    },

    // 404 — Aucun abonnement ne correspond aux informations de résiliation.
    // → Réponse volontairement identique pour un compte inconnu et de
    //   mauvais chiffres : ne JAMAIS distinguer les deux cas à l'écran.
    subscription_not_found: {
        message: 'Ces informations ne correspondent à aucun abonnement.',
        hint: 'Essayez l’autre méthode d’identification : la date du prélèvement au lieu de l’adresse email, ou l’inverse.',
        retryable: false,
    },

    // 409 — Plusieurs personnes correspondent aux informations fournies.
    // → Inviter à contacter le support du site.
    identification_ambiguous: {
        message: 'Plusieurs abonnements correspondent à ces informations.',
        hint: 'Contactez notre support pour que nous identifiions le vôtre.',
        retryable: false,
    },

    // 500 — Erreur interne inattendue.
    server_error: {
        message: 'Une erreur inattendue est survenue.',
        hint: 'Réessayez dans un instant. Si le problème persiste, contactez notre support.',
        retryable: true,
    },
};

/** Repli lorsqu'aucune entrée du catalogue ne correspond (code inédit). */
const FALLBACK: ErrorPresentation = {
    message: 'Une erreur inattendue est survenue.',
    hint: 'Réessayez dans un instant.',
    retryable: true,
};

/**
 * Convertit n'importe quelle erreur levée par le SDK — ou par le réseau — en
 * quelque chose d'affichable.
 *
 * Trois familles sont distinguées :
 *
 * 1. `DashLiteApiError` : l'API a répondu, avec un code du catalogue ;
 * 2. `TypeError` de `fetch` : la requête n'est jamais partie (hors ligne,
 *    domaine non autorisé par le CORS du site, API injoignable) ;
 * 3. le reste : bug de notre côté, on reste générique.
 */
export function presentError(error: unknown): ErrorPresentation {
    if (error instanceof DashLiteApiError) {
        return CATALOGUE[error.code] ?? FALLBACK;
    }

    if (error instanceof TypeError) {
        return {
            message: "Impossible de joindre l'API.",
            hint: "Vérifiez votre connexion. Si le problème persiste, le domaine de ce front n'est peut-être pas déclaré dans les domaines autorisés du site (CORS).",
            retryable: true,
            configuration: true,
        };
    }

    return FALLBACK;
}

/** Le code d'erreur, quand l'erreur vient bien de l'API. */
export function errorCode(error: unknown): string | null {
    return error instanceof DashLiteApiError ? error.code : null;
}

/** Raccourci de lecture : `isApiError(e, 'entitlement_required')`. */
export function isApiError(error: unknown, ...codes: string[]): boolean {
    return error instanceof DashLiteApiError && codes.includes(error.code);
}

/**
 * Extrait les erreurs de validation champ par champ transportées par
 * `details` sur un `422 validation_failed`.
 *
 * L'API renvoie `{ "email": ["…"], "password": ["…"] }` : on n'en garde que
 * le premier message par champ, suffisant pour l'afficher sous l'input.
 */
export function fieldErrors(error: unknown): Record<string, string> {
    if (!(error instanceof DashLiteApiError) || error.code !== 'validation_failed') {
        return {};
    }

    const result: Record<string, string> = {};

    for (const [field, messages] of Object.entries(error.details)) {
        if (Array.isArray(messages) && typeof messages[0] === 'string') {
            result[field] = messages[0];
        } else if (typeof messages === 'string') {
            result[field] = messages;
        }
    }

    return result;
}
